import { Modal } from '@mui/material';
import React, { FC, FormEventHandler, useId } from 'react';

import ComposeFooterContainer from '../../../containers/compose/composeFooter';
import ComposeModalHeaderContainer from '../../../containers/compose/composeModalHeader';
import { ComposeNodeType } from '../../../containers/compose/props';

import useIsComposeOpen from '../../../hooks/router/search/useIsComposeOpen';
import useIsMobile from '../../../hooks/useIsMobile';

import ComposeMeta from '../Common/ComposeMeta';

import * as Styled from './styled';

type Props = {
  formId: string;
  isDraftChanged: boolean;
  isInitialDraftChanged: boolean;
  isPending: boolean;
  nodeType: ComposeNodeType;
  requestClear: () => void;
  requestClose: () => void;
  requestSubmit: () => void;
  setShouldValidate: (shouldValidate: boolean) => void;
  shouldValidate: boolean;
  title: string;
};

/**
 * Compose form base modal
 *
 * @param props                       Props passed to the component
 * @param props.formId                The ID of the form
 * @param props.isDraftChanged        Whether the draft has been changed
 * @param props.isInitialDraftChanged Whether the initial draft has been changed
 * @param props.isPending             Whether the mutation is in progress
 * @param props.nodeType              Type of the compose form
 * @param props.requestClear          Request clearing the compose form
 * @param props.requestClose          Request closing the compose form
 * @param props.requestSubmit         The callback on compose submit
 * @param props.setShouldValidate     Callback to set the shouldValidate state
 * @param props.shouldValidate        Whether the form should be validated
 * @param props.title                 The title of the modal
 *
 * @returns                           Component itself
 */
const ComposeModal: FC<Props> = ({
  formId,
  isDraftChanged,
  isInitialDraftChanged,
  isPending,
  nodeType,
  requestClear,
  requestClose,
  requestSubmit,
  setShouldValidate,
  shouldValidate,
  title,
}) => {
  const isComposeOpen = useIsComposeOpen();
  const isMobile = useIsMobile();
  const titleId = useId();

  const shouldShow = isDraftChanged || isComposeOpen;
  const shouldShowMinimized = isDraftChanged && !isComposeOpen;

  if (!shouldShow) {
    return null;
  }

  const metaProps = { formId, nodeType, shouldValidate };

  /**
   * The user has requested to close the modal
   */
  const onClose = () => {
    requestClose();
    setShouldValidate(false);
  };

  if (shouldShowMinimized) {
    return (
      <Styled.MinimizedModal>
        <ComposeModalHeaderContainer
          fallbackTitle={title}
          nodeType={nodeType}
          onClose={onClose}
          titleId={titleId}
        />
      </Styled.MinimizedModal>
    );
  }

  /**
   * The user has submitted the form,
   * so we request that the message/template is created/updated
   *
   * @param event The event that took place
   */
  const onSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    setShouldValidate(true);

    if (event.currentTarget.checkValidity()) {
      requestSubmit();
      setShouldValidate(false);
      return;
    }

    if (!isMobile) {
      const invalidInput: HTMLElement | null =
        event.currentTarget.querySelector('input:invalid, textarea:invalid');

      invalidInput?.focus();
    }
  };

  return (
    <Modal
      aria-labelledby={titleId}
      disableAutoFocus={true}
      disablePortal={true}
      open={true}
      slotProps={{
        root: {
          style: Styled.WrapperRootStyles,
        },
      }}
    >
      <Styled.ModalWrapper>
        <ComposeModalHeaderContainer
          fallbackTitle={title}
          nodeType={nodeType}
          onClose={onClose}
        />
        <Styled.Content>
          <form id={formId} noValidate={true} onSubmit={onSubmit}>
            <ComposeMeta position="ABOVE" {...metaProps} />
          </form>
          <ComposeMeta position="BELOW" {...metaProps} />
        </Styled.Content>
        <ComposeFooterContainer
          formId={formId}
          isDraftChanged={isInitialDraftChanged}
          isPending={isPending}
          nodeType={nodeType}
          requestClear={requestClear}
        />
      </Styled.ModalWrapper>
    </Modal>
  );
};

export default ComposeModal;
